import { ampli } from '~shared/analytics';
import { downloadFile } from '~shared/lib/download-file';
import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';

import isEmpty from '@tinkoff/utils/is/empty';

import { checkReportStatus, downloadReport } from '.';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import {
  isReportError,
  isReportGenerated,
  isReportInProgress,
} from '../lib/is-report-generated';
import type { CheckReportStatusResponse } from '../model/check-report-status-response';

export const useCheckReportStatusQuery = (
  reportId: string | undefined | null,
  options: Omit<
    UseQueryOptions<CheckReportStatusResponse>,
    'queryKey' | 'queryFn'
  > = {}
) => {
  return useQuery({
    ...options,
    queryKey: ['check-status', reportId],
    queryFn: async () => {
      const response = await checkReportStatus(nonNullableValue(reportId));

      if (isReportError(response)) {
        throw new Error('Check status error');
      }

      return response;
    },
    retry: (count) => count < 3,
    refetchInterval: (query) => {
      const data = query.state.data;
      return isReportInProgress(data) ? 5000 : false;
    },
    enabled: !isEmpty(reportId),
  });
};

type UseDownloadReportQueryProps = {
  reportData?: CheckReportStatusResponse;
  screen: 'payments' | 'applications';
};

export const useDownloadReportQuery = (
  { reportData, screen }: UseDownloadReportQueryProps,
  options: Omit<UseQueryOptions<unknown>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery({
    ...options,
    queryKey: ['download-report', reportData?.reportId],
    queryFn: async () => {
      const data = await downloadReport(nonNullableValue(reportData).reportId);

      downloadFile(
        data,
        `${screen}_details_${format(
          new Date(),
          "yyyy-MM-dd'T'HH:mm:ss.SSS"
        )}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      );

      if (screen === 'applications') {
        ampli.applicationsScreenExportFileDownload({
          fileSize: data.byteLength,
        });
      }
      if (screen === 'payments') {
        ampli.paymentsScreenExportFileDownload({ fileSize: data.byteLength });
      }
      return {};
    },
    enabled: isReportGenerated(reportData),
  });
};
